<script lang="ts">
    import { get } from 'svelte/store';
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';

    import { type StatusApiDataRequestType, WidgetOneApi } from './WidgetOneApi';
    import ProgressClock from './ProgressClock/ProgressClock.svelte';
    import { ChartPopulationOne } from './ChartPopulationOne';
    import { api } from '../../../stores/api';
    import type { DashboardComponentType, WidgetManager } from './WidgetManager';
    import IconFilterAlt from '../Icons/IconFilterAlt.svelte';
    import Opacity from '../Opacity.svelte';
    import * as Sentry from '@sentry/browser';

    export let widgetManager: WidgetManager;
    export let dashboardItem: DashboardComponentType;
    export let chartVisible: boolean = true;

    let error: string = '';
    let loading: boolean = true;
    let chartElement: HTMLDivElement;
    let chart: ChartPopulationOne;
    let queryDescription: string = '';
    let progress: number = 0;
    let previousSettings: StatusApiDataRequestType;

    async function reloadData() {
        loading = true;
        dashboardItem.settings = await WidgetOneApi.parseSettings(
            dashboardItem.settings,
            dashboardItem.we.getWidgetName(),
            widgetManager.mainQuery
        );
        previousSettings = JSON.parse(JSON.stringify(dashboardItem.settings));
        let data = await WidgetOneApi.getData(
            get(api),
            dashboardItem.settings,
            dashboardItem.we.getDataSchema()
        ).catch(error => {
            console.error(error);
        });
        try {
            let newData = dashboardItem.we.callUpdateFunctionForChartData(data);
            loading = false;
            if (chartVisible) chart.render(newData);
        } catch (e) {
            console.error(e);
            Sentry.captureMessage('Error in callUpdateFunctionForChartData', {
                extra: {
                    error: e,
                    data: data,
                },
            });
        }
    }

    onMount(() => {
        (async () => {
            if (chartElement) {
                chart = new ChartPopulationOne(
                    chartElement,
                    dashboardItem.we.getDefaultChartData()
                );
            }
            await reloadData();

            dashboardItem.we.progressClock.reset(dashboardItem.settings.reloadIntervalSeconds);
            dashboardItem.we.progressClock.setIntervalFunction((forceRun: boolean) => {
                progress = dashboardItem.we.progressClock.getRemainingPercent();
                if (dashboardItem.we.progressClock.getRemainingSeconds() <= 0 || forceRun) {
                    progress = 0;
                    reloadData().then(() => {
                        dashboardItem.we.progressClock.reset();
                    });
                }
            }, true);
            dashboardItem.we.progressClock = dashboardItem.we.progressClock;
        })();

        return () => {
            dashboardItem.we.progressClock.stop();
        };
    });

    $: {
        if (dashboardItem) {
            if (dashboardItem.settings.reloadIntervalSeconds === 0) {
                dashboardItem.we.progressClock.stop();
            } else {
                dashboardItem.we.progressClock.reset(dashboardItem.settings.reloadIntervalSeconds);
            }
        }
    }

    $: {
        if (dashboardItem) {
            if (JSON.stringify(previousSettings) !== JSON.stringify(dashboardItem.settings)) {
                console.log('settings changed?', dashboardItem.settings);
                reloadData();
            }
        }
    }
</script>

{#if error}
    <div>{$_(error)}</div>
{:else}
    <div class="widget-header" style="position: relative;">
        <div
            style="position: absolute; right: 0; width: 56px; height: 40px; padding: 2px; margin: 0px 11px; top: 9px;"
        >
            {#if !loading}
                <div
                    style="display: flex; align-items: center; flex-direction: row; column-gap: 7px; justify-content: end;"
                >
                    {#if Object.keys(dashboardItem.settings.query).length > 0}
                        <div
                            style="cursor: pointer"
                            on:click={() => {
                                widgetManager.onSettingsClick(dashboardItem, () => {
                                    widgetManager = widgetManager;
                                });
                            }}
                        >
                            <IconFilterAlt />
                        </div>
                    {/if}
                    {#if dashboardItem.we.progressClock.isRunning() && !loading}
                        <ProgressClock {progress} />
                    {:else}
                        <Opacity opacity={0}>
                            <ProgressClock {progress} />
                        </Opacity>
                    {/if}
                </div>
            {/if}
        </div>
        <div class="widget-header-icon empty-icon"></div>
        <div class="widget-header-title">
            <div
                class="widget-header-title-name"
                style="display: flex; align-items: center; column-gap: 5px;"
            >
                <div>{$_(dashboardItem.we.getTitle())}</div>
            </div>
            {#if queryDescription}
                <div class="widget-header-title-description">
                    ({queryDescription})
                </div>
            {/if}
        </div>
        <div
            class="widget-header-additional"
            style="padding: 16px; font-weight: 600; font-size: 14px;"
        ></div>
    </div>
    {#if chartVisible}
        <div class="ui-widget-content">
            <div
                class="data-summary"
                style="max-width: 480px; min-width: 200px; width: 100%; display: flex; justify-content: space-evenly; align-items: center; padding: 10px 0; margin: 10px 0;"
            >
                <div bind:this={chartElement} style="width: 340px;height:150px;"></div>
            </div>
        </div>
    {/if}
    <slot />
{/if}

<style>
    .ui-widget-content {
        height: 86%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>
